import React, { Component } from 'react'
import { observer } from 'mobx-react'
import {
  Card,
  Button,
  Form,
  Dropdown,
  Message,
  Dimmer,
  Loader,
} from 'semantic-ui-react'
import CircularProgress from '@material-ui/core/CircularProgress'
import StripeCheckout from 'react-stripe-checkout'
import UserModel from 'src/Models/UserModel'
import AddDeviceComponent from 'src/Components/Devices/AddDeviceComponent'
import {
  createOneTimePurchase,
  createUnlimitedPurchase,
  createStripeCustomer,
} from 'src/services/stripe'

export default
@observer
class LicensePurchaseComponent extends Component {
  constructor(props) {
    super(props)

    this.state = {
      selectedMonths: 1,
      isLoading: false,
      error: null,
      success: null,
      pendingPurchaseType: null, // 'monthly' or 'lifetime'
      pendingMonths: null,
      paymentMethodId: null,
    }
  }

  // Generate options for month selection (1-12 months)
  getMonthOptions = () => {
    const options = []
    for (let i = 1; i <= 11; i++) {
      options.push({
        key: i,
        text: `${i} month${i > 1 ? 's' : ''}`,
        value: i,
      })
    }
    return options
  }

  handleMonthChange = (e, { value }) => {
    this.setState({ selectedMonths: value })
  }

  // Handle Stripe token from checkout (simplified approach)
  handleStripeToken = async (token) => {
    try {
      this.setState({ isLoading: true, error: null, success: null })

      const { customerId, forcePaymentMethod = false } = this.props
      const { pendingPurchaseType, pendingMonths } = this.state

      let actualCustomerId = customerId
      let paymentMethodId = null

      if (forcePaymentMethod) {
        // For new customers OR when forcing payment method, always create a fresh customer record
        // This ensures we always have a payment method attached
        const { data } = await createStripeCustomer(
          token.id,
          UserModel.userEmail
        )
        actualCustomerId = data.id
        paymentMethodId = data.defaultPaymentMethod

        console.log('Customer created:', {
          customerId: data.id,
          defaultPaymentMethod: data.defaultPaymentMethod,
          paymentMethodId,
        })

        UserModel.stripeCustomerId = data.id

        // Call parent component to refresh customer data
        if (this.props.onCustomerCreated) {
          this.props.onCustomerCreated(data.id, paymentMethodId)
        }
      }

      // Now proceed with the purchase using the payment method
      if (pendingPurchaseType === 'monthly') {
        await this.executePurchase(
          'monthly',
          actualCustomerId,
          pendingMonths,
          paymentMethodId
        )
      } else if (pendingPurchaseType === 'lifetime') {
        await this.executePurchase(
          'lifetime',
          actualCustomerId,
          null,
          paymentMethodId
        )
      }
    } catch (err) {
      console.error('Payment processing error:', err)
      this.setState({
        error: 'Failed to process payment',
        isLoading: false,
        pendingPurchaseType: null,
        pendingMonths: null,
      })
    }
  }

  // Execute the actual purchase
  executePurchase = async (
    type,
    customerId,
    months = null,
    paymentMethodId = null
  ) => {
    try {
      let response

      // Use provided payment method or the one stored in state or the one from props
      const paymentMethod =
        paymentMethodId ||
        this.state.paymentMethodId ||
        this.props.paymentMethodId

      console.log('executePurchase called with:', {
        type,
        customerId,
        months,
        paymentMethodId,
        finalPaymentMethod: paymentMethod,
      })

      if (type === 'monthly') {
        response = await createOneTimePurchase(
          customerId,
          months,
          paymentMethod
        )
      } else if (type === 'lifetime') {
        response = await createUnlimitedPurchase(customerId, paymentMethod)
      }

      if (response.data) {
        let successMessage
        if (response.data.status === 'succeeded') {
          successMessage =
            type === 'monthly'
              ? `Payment successful! You now have ${months} month${
                  months > 1 ? 's' : ''
                } of access.`
              : 'Payment successful! You now have lifetime access.'
        } else {
          successMessage =
            type === 'monthly'
              ? `Payment intent created for ${months} month${
                  months > 1 ? 's' : ''
                } subscription!`
              : 'Payment intent created for lifetime subscription!'
        }

        this.setState({
          success: successMessage,
          isLoading: false,
          pendingPurchaseType: null,
          pendingMonths: null,
        })

        // Call success callback
        if (this.props.onPurchaseSuccess) {
          this.props.onPurchaseSuccess(response.data, type)
        }
      }
    } catch (error) {
      console.error('Purchase error:', error)
      this.setState({
        error:
          error.response?.data?.message || `Failed to create ${type} payment`,
        isLoading: false,
        pendingPurchaseType: null,
        pendingMonths: null,
      })
    }
  }

  handleMonthlyPurchase = () => {
    const {
      customerId,
      paymentMethodId,
      forcePaymentMethod = false,
    } = this.props
    const { selectedMonths } = this.state

    // If customer exists and NOT forcing payment method, execute purchase directly
    if (customerId && !forcePaymentMethod && paymentMethodId) {
      this.setState({ isLoading: true, error: null, success: null })
      this.executePurchase(
        'monthly',
        customerId,
        selectedMonths,
        paymentMethodId
      )
    } else {
      // Set pending purchase - StripeCheckout will handle the rest
      this.setState({
        pendingPurchaseType: 'monthly',
        pendingMonths: selectedMonths,
        error: null,
        success: null,
      })
    }
  }

  handleLifetimePurchase = () => {
    const {
      customerId,
      paymentMethodId,
      forcePaymentMethod = false,
    } = this.props

    // If customer exists and NOT forcing payment method, execute purchase directly
    if (customerId && !forcePaymentMethod && paymentMethodId) {
      this.setState({ isLoading: true, error: null, success: null })
      this.executePurchase('lifetime', customerId, null, paymentMethodId)
    } else {
      // Set pending purchase - StripeCheckout will handle the rest
      this.setState({
        pendingPurchaseType: 'lifetime',
        pendingMonths: null,
        error: null,
        success: null,
      })
    }
  }

  dismissMessage = () => {
    this.setState({ error: null, success: null })
  }

  calculateMonthlyTotal = () => {
    return this.state.selectedMonths * 40
  }

  renderPurchaseButton = (type, children) => {
    const { customerId, forcePaymentMethod = false } = this.props

    // Always force payment method selection if forcePaymentMethod is true
    // OR if no customer exists
    if (!customerId || forcePaymentMethod) {
      return (
        <StripeCheckout
          email={UserModel.userEmail}
          token={this.handleStripeToken}
          stripeKey={process.env.REACT_APP_STRIPE_PUBLIC_KEY}
          currency="USD"
          name="DCS Unity License"
          description={
            type === 'monthly'
              ? `${this.state.selectedMonths} month subscription`
              : 'Lifetime subscription'
          }
          amount={
            type === 'monthly' ? this.calculateMonthlyTotal() * 100 : 150000
          }
        >
          {children}
        </StripeCheckout>
      )
    }

    // Customer exists and not forcing payment method, return button as-is
    return children
  }

  handleDeviceAdded = (deviceData) => {
    // Callback when a device is added
    console.log('Device added:', deviceData)
    if (this.props.onDeviceAdded) {
      this.props.onDeviceAdded(deviceData)
    }
  }

  render() {
    const { selectedMonths, isLoading, error, success, pendingPurchaseType } =
      this.state
    const { devices = [] } = this.props
    const monthOptions = this.getMonthOptions()
    const monthlyTotal = this.calculateMonthlyTotal()

    // If no devices exist, show device creation form first
    if (devices.length === 0) {
      return (
        <div>
          <AddDeviceComponent
            isRequired={true}
            title="Add Your First Tablet"
            onDeviceAdded={this.handleDeviceAdded}
          />
        </div>
      )
    }

    return (
      <div>
        {error && (
          <Message negative onDismiss={this.dismissMessage}>
            <Message.Header>Error</Message.Header>
            <p>{error}</p>
          </Message>
        )}

        {success && (
          <Message positive onDismiss={this.dismissMessage}>
            <Message.Header>Success</Message.Header>
            <p>{success}</p>
          </Message>
        )}

        {/* Optional: Show add device button for users who want to add more devices */}
        <div style={{ marginBottom: '15px' }}>
          <AddDeviceComponent
            title="Add Another Tablet"
            onDeviceAdded={this.handleDeviceAdded}
          />
        </div>

        <Card.Group itemsPerRow={2}>
          {/* Monthly Plan Card */}
          <Card>
            <Card.Content>
              <Card.Header>Purchase Months</Card.Header>
              <Card.Meta>$40 per month</Card.Meta>
              <Card.Description>
                Choose how many months you want to purchase. Perfect for
                short-term projects or trying out the service.
              </Card.Description>
            </Card.Content>
            <Card.Content extra>
              <Form>
                <Form.Field>
                  <span style={{ marginBottom: '10px' }}>
                    Select number of months:
                  </span>
                  <Dropdown
                    selection
                    options={monthOptions}
                    value={selectedMonths}
                    onChange={this.handleMonthChange}
                    disabled={isLoading}
                  />
                </Form.Field>
                <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                  <strong>Total: ${monthlyTotal}</strong>
                </div>
                {this.renderPurchaseButton(
                  'monthly',
                  <Button
                    primary
                    fluid
                    onClick={this.handleMonthlyPurchase}
                    disabled={isLoading}
                  >
                    {isLoading && pendingPurchaseType === 'monthly' ? (
                      <CircularProgress size={20} />
                    ) : (
                      `Purchase ${selectedMonths} Month${
                        selectedMonths > 1 ? 's' : ''
                      }`
                    )}
                  </Button>
                )}
              </Form>
            </Card.Content>
          </Card>

          {/* Lifetime Plan Card */}
          <Card>
            <Card.Content>
              <Card.Header>Lifetime Plan</Card.Header>
              <Card.Meta>$1,500 one-time payment</Card.Meta>
              <Card.Description>
                Get unlimited access forever. Best value for long-term use and
                professional applications.
              </Card.Description>
            </Card.Content>
            <Card.Content extra>
              <div style={{ marginBottom: '15px' }}>
                <div>
                  <strong>✓ Unlimited access</strong>
                </div>
                <div>
                  <strong>✓ No monthly fees</strong>
                </div>
                <div>
                  <strong>✓ Priority support</strong>
                </div>
              </div>
              {this.renderPurchaseButton(
                'lifetime',
                <Button
                  color="green"
                  fluid
                  onClick={this.handleLifetimePurchase}
                  disabled={isLoading}
                >
                  {isLoading && pendingPurchaseType === 'lifetime' ? (
                    <CircularProgress size={20} />
                  ) : (
                    'Purchase Lifetime Access'
                  )}
                </Button>
              )}
            </Card.Content>
          </Card>
        </Card.Group>

        {/* Loading overlay */}
        <Dimmer active={isLoading} inverted>
          <Loader>
            {pendingPurchaseType
              ? 'Creating customer account...'
              : 'Processing payment...'}
          </Loader>
        </Dimmer>
      </div>
    )
  }
}
